import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useASIItemMaster from '../../../../hooks/useASIItemMaster';
import { showLoading } from 'react-global-loading';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import messages from '../../../../assets/Messages.json';
import Remarks__list from './Remarks__list';
import Remarks from './Remarks';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { filterRemarksList } from '../../../../Custom/Custom';

function FixedAssets() {
  const navigate = useNavigate();



  // Refs to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  // Use the custom hook for Balance Sheet Block Item data
  const {
    blockItemData,
    filteredBlockItems,
    isLoadingBlockItems,
    debouncedBlockItemSearch,
    loadMoreBlockItemData,
    hasMoreBlockItems,
    isLoadingMoreBlockItems,
  } = useASIItemMaster();

  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  // State for Block C Type selection
  const [selectedBlockType, setSelectedBlockType] = useState('grossBlock');

  // State for form data
  const [formData, setFormData] = useState({
    items: Array(50).fill(''),
    grossValues: Array(50).fill(''),
    revaluationAdditions: Array(50).fill(''),
    actualAdditions: Array(50).fill(''),
    deductions: Array(50).fill(''),
    closingAsOn: Array(50).fill(''),
    depreciationBeginning: Array(50).fill(''),
    depreciationDuringYear: Array(50).fill(''),
    adjustments: Array(50).fill(''),
    netValueYearEnd: Array(50).fill(''),
    netValueOpening: Array(50).fill(''),
    netValueClosing: Array(50).fill(''),
    status: Array(50).fill(''),
  });

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });

  const [remarksList, setRemarksList] = useState([]);

  // State to track how many rows are visible
  const [visibleRows, setVisibleRows] = useState(1);
  const MAX_ROWS = 50;

  // State for dropdown management
  const [dropdownStates, setDropdownStates] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [selectedIndex, setSelectedIndex] = useState({});
  const dropdownItemRefs = useRef({});
  const justOpenedRef = useRef({});
  const inputRefs = useRef({});
  const touchStartRef = useRef({});

  // Authentication configuration
  const getAuthConfig = () => {
    let token = localStorage.getItem('jwt');

    // Try alternative token keys if 'jwt' is not found
    if (!token) {
      token = localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('userToken');
    }

    console.log('Raw token from localStorage:', token);

    if (!token) {
      console.error('No authentication token found');
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };
    }

    // Clean the token (remove any extra spaces or quotes)
    const cleanToken = token.replace(/['"]/g, '').trim();
    console.log('Cleaned token:', cleanToken);

    return {
      headers: {
        'Authorization': `Bearer ${cleanToken}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    };
  };

  // Reset refs on component mount to ensure fresh API call
  useEffect(() => {
    // Reset all refs when component mounts
    dataLoadedRef.current = false;
    isLoadingRef.current = false;
    apiCallMadeRef.current = false;
    console.log('🔄 Component mounted - Refs reset for fresh API call');
  }, []);

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    const fetchHeaderData = () => {
      try {
        // Check token availability first
        console.log('=== TOKEN CHECK ON COMPONENT MOUNT ===');
        console.log('All localStorage keys:', Object.keys(localStorage));
        console.log('JWT value (PRIMARY):', localStorage.getItem('jwt'));
        console.log('Token value:', localStorage.getItem('token'));
        console.log('AuthToken value:', localStorage.getItem('authToken'));
        console.log('AccessToken value:', localStorage.getItem('accessToken'));
        console.log('UserToken value:', localStorage.getItem('userToken'));
        console.log('=====================================');

        // Get form data from localStorage (stored by balanceSheet page)
        const balanceSheetData = localStorage.getItem('balanceSheetFormData');

        if (balanceSheetData) {
          const parsedData = JSON.parse(balanceSheetData);

          setHeaderData({
            despatchSlNo: parsedData.dslNo || '',
            permanentSlNo: parsedData.pslNo || '',
            companyName: parsedData.nameUnit || '',
            dslNo: parsedData.dslNo || '',
            surveyYear: parsedData.year || ''
          });
        } else {
          // If no data in localStorage, set defaults
          console.log('No balanceSheet data found in localStorage');
          setHeaderData({
            despatchSlNo: 'DSL No. not set',
            permanentSlNo: 'PSL No. not set',
            companyName: 'Company name not set',
            dslNo: '',
            surveyYear: ''
          });
        }
      } catch (error) {
        console.error('Error fetching data from localStorage:', error);
        setHeaderData({
          despatchSlNo: 'Error loading data',
          permanentSlNo: 'Error loading data',
          companyName: 'Error loading data',
          dslNo: '',
          surveyYear: ''
        });
      }
    };

    fetchHeaderData();
  }, []);

  // Consolidated useEffect for loading existing data - prevents multiple API calls
  useEffect(() => {
    // Check if all conditions are met and API hasn't been called yet
    const shouldLoadData = !dataLoadedRef.current &&
      !isLoadingRef.current &&
      !apiCallMadeRef.current &&
      headerData.despatchSlNo &&
      headerData.despatchSlNo !== 'DSL No. not set' &&
      headerData.despatchSlNo !== 'Error loading data' &&
      headerData.surveyYear &&
      headerData.surveyYear.trim() !== '';

    // Debug logging
    console.log('=== API Call Check ===');
    console.log('dataLoadedRef.current:', dataLoadedRef.current);
    console.log('isLoadingRef.current:', isLoadingRef.current);
    console.log('apiCallMadeRef.current:', apiCallMadeRef.current);
    console.log('headerData.despatchSlNo:', headerData.despatchSlNo);
    console.log('headerData.surveyYear:', headerData.surveyYear);
    console.log('blockItemData.length:', blockItemData.length);
    console.log('shouldLoadData:', shouldLoadData);
    console.log('=====================');

    if (shouldLoadData) {
      // Only load data if token exists
      const token = localStorage.getItem('jwt') || localStorage.getItem('token');
      if (token) {
        console.log('✅ Triggering loadExistingData - all conditions met');
        console.log('DSL No:', headerData.despatchSlNo);
        console.log('Survey Year:', headerData.surveyYear);
        // Mark as called immediately to prevent duplicate calls
        apiCallMadeRef.current = true;
        // Call loadExistingData directly - it will handle its own validation
        loadExistingData();
      } else {
        console.log('❌ No token found, skipping data load');
        // Reset ref if token not found so it can retry
        apiCallMadeRef.current = false;
      }
    } else {
      console.log('❌ Conditions not met for API call');
      if (dataLoadedRef.current) console.log('  - Data already loaded');
      if (isLoadingRef.current) console.log('  - Already loading');
      if (apiCallMadeRef.current) console.log('  - API call already made');
      if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set' || headerData.despatchSlNo === 'Error loading data') console.log('  - DSL No. not available');
      if (!headerData.surveyYear || headerData.surveyYear.trim() === '') console.log('  - Survey Year not available');
    }
  }, [headerData.despatchSlNo, headerData.surveyYear]); // Depend on headerData changes

  // Function to toggle dropdown for specific row
  const toggleDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      const newState = {};
      // Close all other dropdowns and toggle current one
      const isCurrentlyOpen = prev[rowIndex] || false;
      newState[rowIndex] = !isCurrentlyOpen;
      return newState;
    });
    // Reset selected index when opening dropdown
    setSelectedIndex(prev => {
      const currentState = prev[rowIndex];
      if (currentState === undefined || currentState >= 0) {
        return { ...prev, [rowIndex]: -1 };
      }
      return prev;
    });
  };

  // Function to open dropdown (without toggle)
  const openDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      // If already open, don't do anything
      if (prev[rowIndex]) {
        return prev;
      }
      const newState = {};
      // Close all other dropdowns and open current one
      newState[rowIndex] = true;
      // Mark as just opened to prevent immediate toggle
      justOpenedRef.current[rowIndex] = true;
      // Clear the flag after a longer delay for better cross-device compatibility
      setTimeout(() => {
        justOpenedRef.current[rowIndex] = false;
      }, 300);
      return newState;
    });
    // Reset selected index when opening dropdown
    setSelectedIndex(prev => ({ ...prev, [rowIndex]: -1 }));
  };

  // Function to check if dropdown should open upward
  const shouldOpenUpward = (rowIndex) => {
    const inputElement = document.querySelector(`input[data-row="${rowIndex}"]`);
    if (!inputElement) return false;

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200;

    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    return spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
  };

  const getDropdownPosition = (rowIndex) => {
    const inputElement = document.querySelector(`input[data-row="${rowIndex}"]`);
    if (!inputElement) return { top: '100%', bottom: 'auto' };

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      return { top: 'auto', bottom: '100%' };
    } else {
      return { top: '100%', bottom: 'auto' };
    }
  };

  // Function to get dropdown style
  const getDropdownStyle = (rowIndex) => {
    const position = getDropdownPosition(rowIndex);
    const isUpward = shouldOpenUpward(rowIndex);

    return {
      ...position,
      left: 0,
      right: 0,
      zIndex: 99999,
      maxHeight: '200px',
      overflowY: 'auto',
      overflowX: 'hidden',
      minWidth: '250px',
      width: 'max-content',
      maxWidth: '400px',
      border: '1px solid #ddd',
      boxShadow: isUpward ? '0 -4px 6px rgba(0, 0, 0, 0.1)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
      position: 'absolute',
      marginTop: isUpward ? '0' : '2px',
      marginBottom: isUpward ? '2px' : '0',
      transform: 'translateY(0)',
      willChange: 'transform',
      scrollbarWidth: 'thin',
      scrollbarColor: '#ccc #f1f1f1',
      backgroundColor: 'white',
      borderRadius: '4px'
    };
  };

  // Function to close dropdown for specific row
  const closeDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState[rowIndex] = false;
      return newState;
    });
    // Reset selected index when closing dropdown
    setSelectedIndex(prev => ({ ...prev, [rowIndex]: -1 }));
  };

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setDropdownStates({});
    setSelectedIndex({});
  };

  // Handle search input change
  const handleSearchChange = (rowIndex, value) => {
    setSearchTerms(prev => ({
      ...prev,
      [rowIndex]: value
    }));

    // Use the hook's debounced search
    debouncedBlockItemSearch(value);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside dropdown container and dropdown menu
      if (!event.target.closest('.position-relative') &&
        !event.target.closest('.custom-dropdown')) {
        closeAllDropdowns();
      }
    };

    // Add both mouse and touch event listeners for better device compatibility
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);


  // Handle dropdown change for items
  const handleItemChange = (index, selectedItem) => {
    const newItems = [...formData.items];
    const newStatus = [...formData.status];

    if (selectedItem) {
      newItems[index] = selectedItem.item_name || '';
      newStatus[index] = selectedItem.block_item || ''; // Direct mapping since block_item is already formatted

      if (index === visibleRows - 1 && visibleRows < MAX_ROWS) {
        setVisibleRows(visibleRows + 1);
      }

      setFormData({
        ...formData,
        items: newItems,
        status: newStatus
      });
    } else {
      // Reset all fields in this row when item is cleared
      newItems[index] = '';
      newStatus[index] = '';

      setFormData({
        ...formData,
        items: newItems,
        status: newStatus,
        grossValues: [...formData.grossValues].map((val, i) => i === index ? '' : val),
        revaluationAdditions: [...formData.revaluationAdditions].map((val, i) => i === index ? '' : val),
        actualAdditions: [...formData.actualAdditions].map((val, i) => i === index ? '' : val),
        deductions: [...formData.deductions].map((val, i) => i === index ? '' : val),
        closingAsOn: [...formData.closingAsOn].map((val, i) => i === index ? '' : val),
        depreciationBeginning: [...formData.depreciationBeginning].map((val, i) => i === index ? '' : val),
        depreciationDuringYear: [...formData.depreciationDuringYear].map((val, i) => i === index ? '' : val),
        adjustments: [...formData.adjustments].map((val, i) => i === index ? '' : val),
        netValueYearEnd: [...formData.netValueYearEnd].map((val, i) => i === index ? '' : val),
        netValueOpening: [...formData.netValueOpening].map((val, i) => i === index ? '' : val),
        netValueClosing: [...formData.netValueClosing].map((val, i) => i === index ? '' : val)
      });
    }

    // Close dropdown after selection
    closeDropdown(index);
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e, rowIndex, filteredItemsList) => {
    const currentSelected = selectedIndex[rowIndex] !== undefined ? selectedIndex[rowIndex] : -1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[rowIndex]) {
          toggleDropdown(rowIndex);
        }
        // Move selection down
        if (currentSelected < filteredItemsList.length - 1) {
          const newIndex = currentSelected + 1;
          setSelectedIndex(prev => ({ ...prev, [rowIndex]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${rowIndex}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[rowIndex]) {
          toggleDropdown(rowIndex);
        }
        // Move selection up
        if (currentSelected > 0) {
          const newIndex = currentSelected - 1;
          setSelectedIndex(prev => ({ ...prev, [rowIndex]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${rowIndex}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (dropdownStates[rowIndex] && currentSelected >= 0 && currentSelected < filteredItemsList.length) {
          // Select the highlighted item
          handleItemChange(rowIndex, filteredItemsList[currentSelected]);
        } else if (!dropdownStates[rowIndex]) {
          // Open dropdown if closed
          toggleDropdown(rowIndex);
        }
        break;

      case 'Escape':
        e.preventDefault();
        // Close dropdown
        closeDropdown(rowIndex);
        break;

      case 'Tab':
        // Close dropdown on tab
        closeDropdown(rowIndex);
        break;

      default:
        // For other keys, open dropdown if typing
        if (!dropdownStates[rowIndex] && e.key.length === 1) {
          toggleDropdown(rowIndex);
        }
        break;
    }
  };

  const handleInputChange = (index, field, value) => {
    const newFormData = { ...formData };

    // Apply validation based on field type
    let validatedValue = value;

    // Numeric validation for all numeric fields
    if (
      [
        'grossValues',
        'revaluationAdditions',
        'actualAdditions',
        'deductions',
        'closingAsOn',
        'depreciationBeginning',
        'depreciationDuringYear',
        'adjustments',
        'netValueYearEnd',
        'netValueOpening',
        'netValueClosing',
      ].includes(field)
    ) {
      // Allow empty string, numbers, and decimal points (up to 2 decimal places)
      if (value === '' || value === '0' || /^\d*\.?\d*$/.test(value)) {
        // Limit to 2 decimal places
        if (value.includes('.')) {
          const parts = value.split('.');
          if (parts[1] && parts[1].length > 2) {
            validatedValue = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }

        // Limit total length to 15 characters (including decimal point)
        if (validatedValue.length > 15) {
          validatedValue = validatedValue.substring(0, 15);
        }
      } else {
        // If invalid characters, keep previous value
        return;
      }
    }

    newFormData[field][index] = validatedValue;

    // Auto-calculate Column 7: Closing AS On = Cols. 3 + 4 + 5 - 6
    if (
      ['grossValues', 'revaluationAdditions', 'actualAdditions', 'deductions'].includes(
        field
      )
    ) {
      const parseNumber = (val) => {
        if (val === '' || val === null || val === undefined) return 0;
        const num = parseFloat(val);
        return isNaN(num) ? 0 : num;
      };

      const col3 = parseNumber(newFormData.grossValues[index]);
      const col4 = parseNumber(newFormData.revaluationAdditions[index]);
      const col5 = parseNumber(newFormData.actualAdditions[index]);
      const col6 = parseNumber(newFormData.deductions[index]);

      const allEmpty =
        (newFormData.grossValues[index] === '' || newFormData.grossValues[index] === null) &&
        (newFormData.revaluationAdditions[index] === '' ||
          newFormData.revaluationAdditions[index] === null) &&
        (newFormData.actualAdditions[index] === '' ||
          newFormData.actualAdditions[index] === null) &&
        (newFormData.deductions[index] === '' || newFormData.deductions[index] === null);

      if (allEmpty) {
        newFormData.closingAsOn[index] = '';
      } else {
        const closingValue = col3 + col4 + col5 - col6;
        newFormData.closingAsOn[index] = closingValue.toFixed(2);
      }
    }

    // Auto-calculate Column 11: Net value Year End = Cols. 8 + 9 - 10
    if (
      ['depreciationBeginning', 'depreciationDuringYear', 'adjustments'].includes(
        field
      )
    ) {
      const parseNumber = (val) => {
        if (val === '' || val === null || val === undefined) return 0;
        const num = parseFloat(val);
        return isNaN(num) ? 0 : num;
      };

      const col8 = parseNumber(newFormData.depreciationBeginning[index]);
      const col9 = parseNumber(newFormData.depreciationDuringYear[index]);
      const col10 = parseNumber(newFormData.adjustments[index]);

      const allEmptyDep =
        (newFormData.depreciationBeginning[index] === '' ||
          newFormData.depreciationBeginning[index] === null) &&
        (newFormData.depreciationDuringYear[index] === '' ||
          newFormData.depreciationDuringYear[index] === null) &&
        (newFormData.adjustments[index] === '' ||
          newFormData.adjustments[index] === null);

      if (allEmptyDep) {
        newFormData.netValueYearEnd[index] = '';
      } else {
        const netYearEnd = col8 + col9 - col10;
        newFormData.netValueYearEnd[index] = netYearEnd.toFixed(2);
      }
    }

    // Auto-calculate Column 12: Net value opening = Cols. 3 - 8
    if (['grossValues', 'depreciationBeginning'].includes(field)) {
      const parseNumber = (val) => {
        if (val === '' || val === null || val === undefined) return 0;
        const num = parseFloat(val);
        return isNaN(num) ? 0 : num;
      };

      const col3 = parseNumber(newFormData.grossValues[index]);
      const col8 = parseNumber(newFormData.depreciationBeginning[index]);

      const allEmptyNetOpening =
        (newFormData.grossValues[index] === '' ||
          newFormData.grossValues[index] === null) &&
        (newFormData.depreciationBeginning[index] === '' ||
          newFormData.depreciationBeginning[index] === null);

      if (allEmptyNetOpening) {
        newFormData.netValueOpening[index] = '';
      } else {
        const netOpening = col3 - col8;
        newFormData.netValueOpening[index] = netOpening.toFixed(2);
      }
    }

    // Auto-calculate Column 13: Net Value closing = Cols. 7 - 11
    // Column 7 depends on: grossValues, revaluationAdditions, actualAdditions, deductions
    // Column 11 depends on: depreciationBeginning, depreciationDuringYear, adjustments
    // So Column 13 should be calculated when any of these fields change
    if (
      [
        'grossValues',
        'revaluationAdditions',
        'actualAdditions',
        'deductions',
        'depreciationBeginning',
        'depreciationDuringYear',
        'adjustments',
      ].includes(field)
    ) {
      const parseNumber = (val) => {
        if (val === '' || val === null || val === undefined) return 0;
        const num = parseFloat(val);
        return isNaN(num) ? 0 : num;
      };

      // Calculate Column 7: Closing AS On = Cols. 3 + 4 + 5 - 6
      const col3 = parseNumber(newFormData.grossValues[index]);
      const col4 = parseNumber(newFormData.revaluationAdditions[index]);
      const col5 = parseNumber(newFormData.actualAdditions[index]);
      const col6 = parseNumber(newFormData.deductions[index]);
      const col7 = col3 + col4 + col5 - col6;

      // Calculate Column 11: Net value Year End = Cols. 8 + 9 - 10
      const col8 = parseNumber(newFormData.depreciationBeginning[index]);
      const col9 = parseNumber(newFormData.depreciationDuringYear[index]);
      const col10 = parseNumber(newFormData.adjustments[index]);
      const col11 = col8 + col9 - col10;

      // Check if all fields are empty
      const allEmptyCol7 =
        (newFormData.grossValues[index] === '' ||
          newFormData.grossValues[index] === null) &&
        (newFormData.revaluationAdditions[index] === '' ||
          newFormData.revaluationAdditions[index] === null) &&
        (newFormData.actualAdditions[index] === '' ||
          newFormData.actualAdditions[index] === null) &&
        (newFormData.deductions[index] === '' ||
          newFormData.deductions[index] === null);

      const allEmptyCol11 =
        (newFormData.depreciationBeginning[index] === '' ||
          newFormData.depreciationBeginning[index] === null) &&
        (newFormData.depreciationDuringYear[index] === '' ||
          newFormData.depreciationDuringYear[index] === null) &&
        (newFormData.adjustments[index] === '' ||
          newFormData.adjustments[index] === null);

      if (allEmptyCol7 && allEmptyCol11) {
        newFormData.netValueClosing[index] = '';
      } else {
        const netClosing = col7 - col11;
        newFormData.netValueClosing[index] = netClosing.toFixed(2);
      }
    }

    setFormData(newFormData);
  };

  // formatStatusFlag function removed - direct mapping used since block_item is already formatted

  // Function to reset form data
  const handleReset = () => {
    setFormData({
      items: Array(50).fill(''),
      grossValues: Array(50).fill(''),
      revaluationAdditions: Array(50).fill(''),
      actualAdditions: Array(50).fill(''),
      deductions: Array(50).fill(''),
      closingAsOn: Array(50).fill(''),
      depreciationBeginning: Array(50).fill(''),
      depreciationDuringYear: Array(50).fill(''),
      adjustments: Array(50).fill(''),
      netValueYearEnd: Array(50).fill(''),
      netValueOpening: Array(50).fill(''),
      netValueClosing: Array(50).fill(''),
      status: Array(50).fill(''),
    });

    setVisibleRows(1);
    setDropdownStates({});
    setSearchTerms({});
    setSelectedBlockType('grossBlock');

    // Clear API call references
    dataLoadedRef.current = false;
    isLoadingRef.current = false;
    apiCallMadeRef.current = false;

    toast.success('Form reset successfully!', messages.COLOR_RED);
  };

  // Load existing data from API
  const loadExistingData = async () => {
    // Validate required data before making API call
    if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set' || headerData.despatchSlNo === 'Error loading data' || !headerData.surveyYear || headerData.surveyYear.trim() === '') {
      console.log('❌ loadExistingData: Missing required data - DSL No. or Survey Year');
      console.log('DSL No:', headerData.despatchSlNo);
      console.log('Survey Year:', headerData.surveyYear);
      // Reset refs if validation fails
      apiCallMadeRef.current = false;
      return;
    }

    // Prevent multiple calls - check refs first (double check)
    if (isLoadingRef.current) {
      console.log('loadExistingData: Already loading, skipping...');
      return;
    }

    // Mark as loading immediately to prevent duplicate calls
    isLoadingRef.current = true;

    // Show global loading
    showLoading(true);

    try {
      const payload = {
        dsl_no: headerData.despatchSlNo,
        survey_year: headerData.surveyYear.split('-')[0] // Extract year from format like "2022-23"
      };

      console.log('🚀 Loading existing data with payload:', payload);
      console.log('API URL:', `${API_URL}api/blockc/get-data`);
      const response = await axios.post(`${API_URL}api/blockc/get-data`, payload, getAuthConfig());
      console.log('✅ GET API Response:', response.data);

      if (response.data && response.data.status && response.data.data) {
        const existingData = response.data.data;

        // Initialize form data with existing values
        const newFormData = {
          items: Array(50).fill(''),
          grossValues: Array(50).fill(''),
          revaluationAdditions: Array(50).fill(''),
          actualAdditions: Array(50).fill(''),
          deductions: Array(50).fill(''),
          closingAsOn: Array(50).fill(''),
          depreciationBeginning: Array(50).fill(''),
          depreciationDuringYear: Array(50).fill(''),
          adjustments: Array(50).fill(''),
          netValueYearEnd: Array(50).fill(''),
          netValueOpening: Array(50).fill(''),
          netValueClosing: Array(50).fill(''),
          status: Array(50).fill(''),
        };

        // Populate with existing data - mapping API response to form fields
        existingData.forEach((item, index) => {
          if (index < MAX_ROWS) {
            // Find matching item in dropdown data using itemName (for fallback if needed)
            const matchingItem = blockItemData.find(dropdownItem =>
              dropdownItem.item_name === item.itemName
            );

            // Always use itemName from API
            newFormData.items[index] = item.itemName || '';

            // Always use blockItem from API for status field (it's the source of truth)
            // If block_item is empty in API, use matching item from dropdown
            newFormData.status[index] = item.block_item || (matchingItem ? matchingItem.block_item : '');

            // Map API response fields to form fields (preserve 0 values)
            // New API format uses snake_case field names
            newFormData.grossValues[index] = item.gross_value_opening ?? '';
            newFormData.revaluationAdditions[index] = item.addition_revaluation ?? '';
            newFormData.actualAdditions[index] = item.actual_additions ?? '';
            newFormData.deductions[index] = item.deductions_adjustment ?? '';
            newFormData.closingAsOn[index] = item.closing_total ?? '';
            newFormData.depreciationBeginning[index] = item.depreciation_beginning ?? '';
            newFormData.depreciationDuringYear[index] = item.depreciation_during_year ?? '';
            newFormData.adjustments[index] = item.adjustment_sold_discarded ?? '';
            newFormData.netValueYearEnd[index] = item.net_value_year_end ?? '';
            newFormData.netValueOpening[index] = item.net_value_opening ?? '';
            newFormData.netValueClosing[index] = item.net_value_closing ?? '';
          }
        });

        // Don't map remarks data from API - keep fields blank when loading data
        // Map API response to Remarks List from remarks array
        let mappedRemarksList = [];
        if (response.data.remarks && Array.isArray(response.data.remarks)) {
          mappedRemarksList = response.data.remarks.map((remark, index) => {
            // Extract block letter from "BLOCK C" format (e.g., "BLOCK C" -> "C")
            let blockLetter = '';
            if (remark.block) {
              // Remove "BLOCK " prefix and get the letter
              blockLetter = remark.block.replace(/^BLOCK\s+/i, '').trim();
            }

            return {
              slNo: index + 1,
              blocks: blockLetter || remark.block || '',
              columns: remark.columnsName || '',
              items: remark.Items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: remark.createdDate || remark.createdTime || '',
              remarksBy: remark.updatedBy || '',
              role: remark.updatedBy || ''
            };
          });
          setRemarksList(mappedRemarksList);
        } else {
          // Fallback: if remarks array is not available, use empty array
          setRemarksList([]);
        }

        // Set default in Items dropdown based on remarksList availability
        // If remarksList is empty, set "SELECT_REMARKS", otherwise set "ALL"
        setRemarksData({
          remarksBlock: (mappedRemarksList.length > 0) ? 'ALL' : 'SELECT_REMARKS',
          remarksItems: '',
          remarks: ''
        });

        setFormData(newFormData);

        // Set visible rows based on data
        const filledRows = existingData.length;
        setVisibleRows(Math.max(1, Math.min(MAX_ROWS, filledRows + 1)));

        dataLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Error loading existing data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        console.log('No existing data found');
      } else {
        toast.error('Error loading data. Please try again.', messages.COLOR_RED);
      }
    } finally {
      isLoadingRef.current = false;
      // Don't reset apiCallMadeRef here - let it stay true to prevent duplicate calls
      // Hide global loading
      showLoading(false);
    }
  };

  // Save data to API
  const handleSaveToAPI = async () => {
    if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set') {
      toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
      return false;
    }

    // Check if token exists before making API call
    let token = localStorage.getItem('jwt'); // Use JWT token first

    // Try alternative token keys if 'jwt' is not found
    if (!token) {
      token = localStorage.getItem('token') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('userToken');
    }

    console.log('All localStorage keys:', Object.keys(localStorage));
    console.log('Token value:', token);
    console.log('Token type:', typeof token);
    console.log('Token length:', token ? token.length : 0);

    if (!token) {
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return false;
    }

    console.log('Starting save process with token:', token ? 'Token exists' : 'No token');
    console.log('DSL No:', headerData.despatchSlNo);

    // Show global loading
    showLoading(true);

    try {
      // Helper function to check if value is empty (handles both string and number)
      // For numeric fields, 0 is a valid value, so we only check for null, undefined, empty string, or NaN
      const isEmpty = (value) => {
        // Check for null or undefined
        if (value === null || value === undefined) {
          return true;
        }

        // Check for number type
        if (typeof value === 'number') {
          // 0 is a valid number, only NaN is considered empty
          return isNaN(value);
        }

        // Check for string type
        if (typeof value === 'string') {
          const trimmed = value.trim();
          // Empty string is empty, but "0" or "0.0" are valid values
          if (trimmed === '') {
            return true;
          }
          // "0" is a valid value, not empty
          if (trimmed === '0' || trimmed === '0.0' || trimmed === '0.00') {
            return false;
          }
          // Any other non-empty string is valid
          return false;
        }

        // For any other type, consider it not empty
        return false;
      };

      // Validate mandatory fields for Gross Block
      // Mandatory columns: Types of Assets (Col-2), Actual Addition (Col-5), Depreciation beginning (Col-8), Depreciation during the year (Col-9)
      if (selectedBlockType === 'grossBlock') {
        for (let i = 0; i < visibleRows; i++) {
          // Skip validation for completely empty rows (no item selected and no other data)
          const hasItem = formData.items[i] && typeof formData.items[i] === 'string' && formData.items[i].trim() !== '';
          const hasOtherData = formData.grossValues[i] || formData.actualAdditions[i] || formData.depreciationBeginning[i] ||
            formData.depreciationDuringYear[i] || formData.closingAsOn[i] || formData.netValueYearEnd[i];

          // Only validate if row has some data (either item selected or other fields filled)
          if (hasItem || hasOtherData) {
            // Col-2: Types of Assets (items) - MANDATORY (only if row has data)
            if (!hasItem) {
              toast.error('Please enter data in column 2 (Types of Assets)', messages.COLOR_RED);
              showLoading(false);
              return false;
            }

            // Col-5: Actual Addition (actualAdditions) - MANDATORY
            const col5Val = formData.actualAdditions[i];
            if (isEmpty(col5Val)) {
              toast.error('Please enter data in column 5 (Actual Addition)', messages.COLOR_RED);
              showLoading(false);
              return false;
            }

            // Col-8: Depreciation beginning (depreciationBeginning) - MANDATORY
            const col8Val = formData.depreciationBeginning[i];
            if (isEmpty(col8Val)) {
              toast.error('Please enter data in column 8 (Depreciation beginning)', messages.COLOR_RED);
              showLoading(false);
              return false;
            }

            // Col-9: Depreciation during the year (depreciationDuringYear) - MANDATORY
            const col9Val = formData.depreciationDuringYear[i];
            if (isEmpty(col9Val)) {
              toast.error('Please enter data in column 9 (Depreciation during the year)', messages.COLOR_RED);
              showLoading(false);
              return false;
            }
          }
        }
      }

      const dataToSave = [];

      // Helper function to safely parse number, defaulting to 0 if invalid
      const safeParseFloat = (value) => {
        // Handle null, undefined, or empty string
        if (value === null || value === undefined || value === '') return 0;
        // Convert to string and trim to handle whitespace
        const stringValue = String(value).trim();
        if (stringValue === '') return 0;
        // Parse the float value
        const parsed = parseFloat(stringValue);
        return isNaN(parsed) ? 0 : parsed;
      };

      const safeParseInt = (value) => {
        if (!value || value === '' || value === null || value === undefined) return 0;
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? 0 : parsed;
      };

      // Parse survey year safely (outside loop so it's accessible for remarks array)
      const surveyYearValue = headerData.surveyYear
        ? safeParseInt(headerData.surveyYear.split('-')[0])
        : 0;

      // Parse dslNo as number (outside loop so it's accessible for remarks array)
      const dslNoParsed = headerData.despatchSlNo ? safeParseInt(headerData.despatchSlNo) : 0;

      for (let i = 0; i < visibleRows; i++) {
        const item = {
          item_name: formData.items[i] || '',
          gross_value_opening: formData.grossValues[i] ?? '',
          revaluation_additions: formData.revaluationAdditions[i] ?? '',
          actual_additions: formData.actualAdditions[i] ?? '',
          deductions_adjustment: formData.deductions[i] ?? '',
          closing_as_on: formData.closingAsOn[i] ?? '',
          depreciation_beginning: formData.depreciationBeginning[i] ?? '',
          depreciation_during_year: formData.depreciationDuringYear[i] ?? '',
          adjustment_sold_discarded: formData.adjustments[i] ?? '',
          net_value_year_end: formData.netValueYearEnd[i] ?? '',
          net_value_opening: formData.netValueOpening[i] ?? '',
          net_value_closing: formData.netValueClosing[i] ?? '',
          block_item: formData.status[i] || '',
        };

        // Only add if at least one field has data
        if (item.item_name || item.gross_value_opening || item.revaluation_additions || item.actual_additions ||
          item.depreciation_during_year || item.closing_as_on || item.net_value_year_end ||
          item.net_value_opening || item.net_value_closing) {

          // Extract item_code from block_item (e.g., "Block-C, Item-7" -> "7")
          let itemCode = '';
          if (item.block_item && item.block_item.includes('Item-')) {
            const match = item.block_item.match(/Item-(\d+)/);
            itemCode = match ? match[1] : '';
          }

          // Fallback: Find item_code from blockItemData if not found in block_item
          if (!itemCode) {
            const matchingItem = blockItemData.find(dropdownItem =>
              dropdownItem.item_name === item.item_name
            );
            itemCode = matchingItem ? matchingItem.item_code || '' : '';
          }

          // Parse all numeric values safely
          const openingAsOn = safeParseFloat(item.gross_value_opening);
          const dueToRevaluation = safeParseFloat(item.revaluation_additions);
          const actualAdditions = safeParseFloat(item.actual_additions);
          const deductionAdjDuringYear = safeParseFloat(item.deductions_adjustment);
          const closingAsOn = safeParseFloat(item.closing_as_on);
          const uptoYearBeginning = safeParseFloat(item.depreciation_beginning);
          const providedDuringYear = safeParseFloat(item.depreciation_during_year);
          const adjForSoldDiscardedDuringYear = safeParseFloat(item.adjustment_sold_discarded);
          const netValueYearEnd = safeParseFloat(item.net_value_year_end);
          const netValueOpening = safeParseFloat(item.net_value_opening);
          const netValueClosing = safeParseFloat(item.net_value_closing);

          // Calculate uptoYearEnd: uptoYearBeginning + providedDuringYear - adjForSoldDiscardedDuringYear
          const uptoYearEnd = uptoYearBeginning + providedDuringYear - adjForSoldDiscardedDuringYear;

          // Calculate openingAsOnNet: openingAsOn - uptoYearBeginning (Net value at opening)
          const openingAsOnNet = openingAsOn - uptoYearBeginning;

          // Calculate closingAsOnNet: closingAsOn - uptoYearEnd (Net value at closing)
          const closingAsOnNet = closingAsOn - uptoYearEnd;

          // Calculate assetCode: itemCode + 139
          // Based on examples: itemCode 2 -> assetCode 141, itemCode 5 -> assetCode 144
          const parsedItemCode = itemCode ? safeParseInt(itemCode) : 0;
          const assetCode = parsedItemCode > 0 ? (parsedItemCode + 139) : 0;

          // Determine block_type based on selectedBlockType
          const blockType = selectedBlockType === 'grossBlock' ? 'gross' : 'net';

          // Use blockItem from status column in format "Block-C,Item-5" (remove space after comma if exists)
          let blockItemFormatted = (item.block_item || '').replace(/, /g, ',');

          const payload = {
            dslNo: dslNoParsed,
            surveyYear: surveyYearValue,
            itemCode: parsedItemCode,
            assetCode: assetCode,
            itemName: item.item_name || '',
            blockItem: blockItemFormatted,
            blockType: blockType,
            grossValueOpening: openingAsOn,
            additionRevaluation: dueToRevaluation,
            actualAdditions: actualAdditions,
            deductionAdjDuringYear: deductionAdjDuringYear,
            closingAsOn: closingAsOn,
            depreciationBeginning: uptoYearBeginning,
            depreciationDuringYear: providedDuringYear,
            adjForSoldDiscardedDuringYear: adjForSoldDiscardedDuringYear,
            netValueOpening: netValueOpening,
            netValueYearEnd: netValueYearEnd,
            netValueClosing: netValueClosing,
            tableName: 'Block C',
            statusFlag: '1'
          };

          dataToSave.push(payload);
        }
      }

      if (dataToSave.length === 0) {
        toast.warning('No data to save', messages.COLOR_RED);
        return false;
      }

      // Prepare remarks array
      const remarksArray = [];
      if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
        remarksArray.push({
          dslNo: dslNoParsed,
          surveyYear: surveyYearValue,
          block: 'C',
          columnsName: remarksData.remarksItems || '',
          Items: remarksData.remarksBlock || '', // Item name from Items dropdown
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with data and remarks arrays
      const finalPayload = {
        data: dataToSave,
        remarks: remarksArray
      };

      console.log('Sending data to API:', finalPayload);
      console.log('API URL:', `${API_URL}api/blockc/insert-item`);

      // Get fresh token and create auth config
      let freshToken = localStorage.getItem('jwt'); // Use JWT token first
      if (!freshToken) {
        freshToken = localStorage.getItem('token') ||
          localStorage.getItem('authToken') ||
          localStorage.getItem('accessToken') ||
          localStorage.getItem('userToken');
      }

      console.log('Fresh token retrieved:', freshToken);

      if (!freshToken) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return false;
      }

      const cleanToken = freshToken.replace(/['"]/g, '').trim();
      console.log('Clean token for API:', cleanToken);

      const authConfig = {
        headers: {
          'Authorization': `Bearer ${cleanToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      };

      console.log('Auth headers:', authConfig);
      console.log('Token in header:', authConfig.headers?.Authorization);

      // Add request interceptor to verify token is being sent
      const requestInterceptor = axios.interceptors.request.use(
        (config) => {
          console.log('Request interceptor - Headers being sent:', config.headers);
          console.log('Request interceptor - Authorization header:', config.headers?.Authorization);
          return config;
        },
        (error) => {
          console.error('Request interceptor error:', error);
          return Promise.reject(error);
        }
      );

      const response = await axios.post(`${API_URL}api/blockc/insert-item`, finalPayload, authConfig);

      // Remove the interceptor after use
      axios.interceptors.request.eject(requestInterceptor);

      console.log('API Response:', response.data);

      if (response.data && response.data.status) {
        toast.success('Data saved successfully!', messages.COLOR_RED);
        // Mark page as saved for green header
        markPageAsSaved(PAGE_NAMES.FIXED_ASSETS);
        // Refresh balance sheet validation to update header colors
        if (headerData.dslNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.dslNo, headerData.surveyYear);
        }
        return true;
      } else {
        toast.error('Failed to save data', messages.COLOR_RED);
        return false;
      }
    } catch (error) {
      console.error('Error saving data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions or contact administrator.', messages.COLOR_RED);
        console.error('403 Error Details:', error.response?.data);
      } else if (error.response?.status === 400) {
        toast.error('Invalid data. Please check your inputs.', messages.COLOR_RED);
        console.error('400 Error Details:', error.response?.data);
      } else {
        toast.error(`Error saving data: ${error.message}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      // Hide global loading
      showLoading(false);
    }
  };

  const generateTableRows = () => {
    const rows = [];
    // Use hook data only
    const itemsToUse = blockItemData;
    const currentFilteredItems = filteredBlockItems;

    // Get already selected items to filter them out
    const selectedItems = formData.items.filter(item => item && item.trim() !== '');

    for (let i = 0; i < visibleRows; i++) {

      rows.push(
        <tr key={i}>
          <td style={{ textAlign: 'center', fontWeight: '500' }}>{i + 1}</td>
          <td>
            <div className="position-relative" style={{ position: 'relative', overflow: 'visible' }}>
              <input
                ref={(el) => {
                  if (el) {
                    inputRefs.current[i] = el;
                  }
                }}
                type="text"
                className="form-control"
                value={formData.items[i] || ''}
                placeholder={isLoadingBlockItems ? 'Loading...' : 'Select Item'}
                data-row={i}
                style={{
                  textAlign: 'center',
                  paddingRight: '30px',
                  fontSize: '12px',
                  minHeight: '32px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                onChange={(e) => {
                  handleSearchChange(i, e.target.value);
                  // Update form data for manual input
                  const newItems = [...formData.items];
                  const newStatus = [...formData.status];
                  newItems[i] = e.target.value;

                  // Clear status if item is cleared
                  if (e.target.value.trim() === '') {
                    newStatus[i] = '';
                    // Reset all fields in this row when item is cleared
                    setFormData({
                      ...formData,
                      items: newItems,
                      status: newStatus,
                      grossValues: [...formData.grossValues].map((val, idx) => idx === i ? '' : val),
                      revaluationAdditions: [...formData.revaluationAdditions].map((val, idx) => idx === i ? '' : val),
                      actualAdditions: [...formData.actualAdditions].map((val, idx) => idx === i ? '' : val),
                      deductions: [...formData.deductions].map((val, idx) => idx === i ? '' : val),
                      closingAsOn: [...formData.closingAsOn].map((val, idx) => idx === i ? '' : val),
                      depreciationBeginning: [...formData.depreciationBeginning].map((val, idx) => idx === i ? '' : val),
                      depreciationDuringYear: [...formData.depreciationDuringYear].map((val, idx) => idx === i ? '' : val),
                      adjustments: [...formData.adjustments].map((val, idx) => idx === i ? '' : val),
                      netValueYearEnd: [...formData.netValueYearEnd].map((val, idx) => idx === i ? '' : val),
                      netValueOpening: [...formData.netValueOpening].map((val, idx) => idx === i ? '' : val),
                      netValueClosing: [...formData.netValueClosing].map((val, idx) => idx === i ? '' : val)
                    });
                  } else {
                    setFormData({ ...formData, items: newItems, status: newStatus });
                  }
                  // Reset selected index when typing
                  setSelectedIndex(prev => ({ ...prev, [i]: -1 }));
                }}
                onFocus={(e) => {
                  // Open dropdown on focus if it's closed
                  if (!dropdownStates[i]) {
                    openDropdown(i);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Only toggle if dropdown wasn't just opened
                  if (!justOpenedRef.current[i]) {
                    toggleDropdown(i);
                  } else {
                    // Clear the flag since we handled it
                    justOpenedRef.current[i] = false;
                  }
                }}
                onMouseDown={(e) => {
                  // Open dropdown immediately on mousedown to ensure it opens on first click
                  if (!dropdownStates[i] && !touchStartRef.current[i]) {
                    openDropdown(i);
                  }
                }}
                onTouchStart={(e) => {
                  // Mark as touch interaction
                  touchStartRef.current[i] = true;
                  setTimeout(() => {
                    touchStartRef.current[i] = false;
                  }, 500);

                  // Open dropdown on touch
                  if (!dropdownStates[i]) {
                    e.preventDefault(); // Prevent mouse events from firing
                    openDropdown(i);
                  }
                }}
                onTouchEnd={(e) => {
                  // If dropdown is closed, prevent the click event from firing
                  if (!dropdownStates[i]) {
                    e.preventDefault();
                  }
                }}
                onKeyDown={(e) => {
                  const filteredList = currentFilteredItems
                    .filter(singleItem => {
                      const isAlreadySelected = selectedItems.some((selectedItem, selectedIndex) =>
                        selectedIndex !== i && selectedItem === singleItem.item_name
                      );
                      return !isAlreadySelected;
                    });
                  handleKeyDown(e, i, filteredList);
                }}
                disabled={isLoadingBlockItems}
              />
              <i className="fa fa-chevron-down position-absolute"
                style={{
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  pointerEvents: 'none'
                }}></i>
              {dropdownStates[i] && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm custom-dropdown"
                  style={getDropdownStyle(i)}
                >
                  {currentFilteredItems.length > 0 ? (
                    <>
                      {currentFilteredItems
                        .filter(singleItem => {
                          // Filter out already selected items (except current row)
                          const isAlreadySelected = selectedItems.some((selectedItem, selectedIndex) =>
                            selectedIndex !== i && selectedItem === singleItem.item_name
                          );
                          return !isAlreadySelected;
                        })
                        .map((singleItem, index) => {
                          const isSelected = selectedIndex[i] === index;
                          return (
                            <div
                              key={index}
                              ref={(el) => {
                                if (el) {
                                  dropdownItemRefs.current[`${i}-${index}`] = el;
                                }
                              }}
                              className="px-3 py-2 cursor-pointer"
                              style={{
                                cursor: 'pointer',
                                borderBottom: '1px solid #f0f0f0',
                                fontSize: '12px',
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                minHeight: '40px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                outline: isSelected ? '2px solid #1976d2' : 'none',
                                outlineOffset: '-2px',
                                touchAction: 'manipulation' // Improves touch responsiveness
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleItemChange(i, singleItem);
                              }}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleItemChange(i, singleItem);
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                setSelectedIndex(prev => ({ ...prev, [i]: index }));
                              }}
                              onMouseLeave={(e) => {
                                if (!isSelected) {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }
                              }}
                              tabIndex={-1}
                            >
                              <div style={{
                                fontWeight: 'bold',
                                marginBottom: '2px',
                                wordBreak: 'break-word',
                                lineHeight: '1.3'
                              }}>
                                {singleItem.item_name}
                              </div>
                              {singleItem.block_item && (
                                <div style={{
                                  color: '#666',
                                  fontSize: '11px',
                                  wordBreak: 'break-word',
                                  lineHeight: '1.2'
                                }}>
                                  {singleItem.block_item}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      {hasMoreBlockItems && (
                        <div
                          className="px-3 py-2 text-center text-muted"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            loadMoreBlockItemData();
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            loadMoreBlockItemData();
                          }}
                          style={{
                            cursor: 'pointer',
                            touchAction: 'manipulation'
                          }}
                        >
                          {isLoadingMoreBlockItems ? 'Loading more...' : 'Load more items'}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
                      {isLoadingBlockItems ? 'Loading items...' : 'No items found'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </td>
          <td><input
            type="text"
            className="form-control"
            value={formData.grossValues[i]}
            onChange={(e) => handleInputChange(i, 'grossValues', e.target.value)}
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled={!formData.items[i] || formData.items[i].trim() === ''}
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: (!formData.items[i] || formData.items[i].trim() === '') ? '#f5f5f5' : '#fff',
              cursor: (!formData.items[i] || formData.items[i].trim() === '') ? 'not-allowed' : 'text'
            }}
          /></td>
          {/* <td><input 
            type="text" 
            className="form-control" 
            value={formData.netValues[i]} 
            onChange={(e) => handleInputChange(i, 'netValues', e.target.value)} 
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            style={{ fontSize: '12px', minHeight: '32px', textAlign: 'right' }} 
          /></td> */} {/* Commented out - column removed */}
          <td>
            <input
              type="text"
              className="form-control"
              value={formData.revaluationAdditions[i]}
              onChange={(e) => handleInputChange(i, 'revaluationAdditions', e.target.value)}
              maxLength="15"
              pattern="[0-9]*\.?[0-9]*"
              disabled={!formData.items[i] || formData.items[i].trim() === '' || selectedBlockType === 'netBlock'}
              style={(!formData.items[i] || formData.items[i].trim() === '' || selectedBlockType === 'netBlock') ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontSize: '12px', minHeight: '32px' } : { fontSize: '12px', minHeight: '32px', textAlign: 'right' }}
            />
          </td>
          <td><input
            type="text"
            className="form-control"
            value={formData.actualAdditions[i]}
            onChange={(e) => handleInputChange(i, 'actualAdditions', e.target.value)}
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled={!formData.items[i] || formData.items[i].trim() === ''}
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: (!formData.items[i] || formData.items[i].trim() === '') ? '#f5f5f5' : '#fff',
              cursor: (!formData.items[i] || formData.items[i].trim() === '') ? 'not-allowed' : 'text'
            }}
          /></td>
          <td><input
            type="text"
            className="form-control"
            value={formData.deductions[i]}
            onChange={(e) => handleInputChange(i, 'deductions', e.target.value)}
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled={!formData.items[i] || formData.items[i].trim() === ''}
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: (!formData.items[i] || formData.items[i].trim() === '') ? '#f5f5f5' : '#fff',
              cursor: (!formData.items[i] || formData.items[i].trim() === '') ? 'not-allowed' : 'text'
            }}
          /></td>
          <td><input
            type="text"
            className="form-control"
            value={formData.closingAsOn[i]}
            readOnly
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: '#f5f5f5',
              cursor: 'not-allowed'
            }}
          /></td>
          <td>
            <input
              type="text"
              className="form-control"
              value={formData.depreciationBeginning[i]}
              onChange={(e) => handleInputChange(i, 'depreciationBeginning', e.target.value)}
              maxLength="15"
              pattern="[0-9]*\.?[0-9]*"
              disabled={!formData.items[i] || formData.items[i].trim() === '' || selectedBlockType === 'netBlock'}
              style={(!formData.items[i] || formData.items[i].trim() === '' || selectedBlockType === 'netBlock') ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontSize: '12px', minHeight: '32px' } : { fontSize: '12px', minHeight: '32px', textAlign: 'right' }}
            />
          </td>
          <td><input
            type="text"
            className="form-control"
            value={formData.depreciationDuringYear[i]}
            onChange={(e) => handleInputChange(i, 'depreciationDuringYear', e.target.value)}
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled={!formData.items[i] || formData.items[i].trim() === ''}
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: (!formData.items[i] || formData.items[i].trim() === '') ? '#f5f5f5' : '#fff',
              cursor: (!formData.items[i] || formData.items[i].trim() === '') ? 'not-allowed' : 'text'
            }}
          /></td>
          <td>
            <input
              type="text"
              className="form-control"
              value={formData.adjustments[i]}
              onChange={(e) => handleInputChange(i, 'adjustments', e.target.value)}
              maxLength="15"
              pattern="[0-9]*\.?[0-9]*"
              disabled={!formData.items[i] || formData.items[i].trim() === '' || selectedBlockType === 'netBlock'}
              style={(!formData.items[i] || formData.items[i].trim() === '' || selectedBlockType === 'netBlock') ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontSize: '12px', minHeight: '32px' } : { fontSize: '12px', minHeight: '32px', textAlign: 'right' }}
            />
          </td>
          <td><input
            type="text"
            className="form-control"
            value={formData.netValueYearEnd[i]}
            readOnly
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: '#f5f5f5',
              cursor: 'not-allowed'
            }}
          /></td>
          <td><input
            type="text"
            className="form-control"
            value={formData.netValueOpening[i]}
            readOnly
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: '#f5f5f5',
              cursor: 'not-allowed'
            }}
          /></td>
          <td><input
            type="text"
            className="form-control"
            value={formData.netValueClosing[i]}
            readOnly
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'right',
              backgroundColor: '#f5f5f5',
              cursor: 'not-allowed'
            }}
          /></td>
          <td><input type="text" className="form-control" value={formData.status[i] || ''} disabled style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontSize: '11px', minHeight: '32px', textAlign: 'center', fontWeight: '500', color: '#495057' }} /></td>
        </tr>
      );
    }
    return rows;
  };

  // Filter remarks list using centralized function from Custom.js
  const filteredRemarksList = useMemo(() => {
    return filterRemarksList(remarksList, remarksData);
  }, [remarksList, remarksData]);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      <div className="container-fluid py-3">
        {/* Header Section - Direct Display */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-12 mb-3">
                <h4 className="page_heading mb-3 pb-2 border-bottom">
                  ASI Schedule 2024-25 <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>CONFIDENTIAL <i className="fa fa-lock me-2" style={{ fontSize: '20px', color: '#1976d2' }}></i></span>
                </h4>
                <h5 className="mb-0 pb-3 border-bottom"><span style={{ color: '#1976d2', fontWeight: 'bold' }}>Fixed Assets Data for</span> <strong>Block C</strong></h5>
              </div>
            </div>
            <div className="row">
              <div className="col-md-8">
                <div className="row g-3">
                  <div className="col-md-3">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>DSL No.:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.despatchSlNo}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>PSL No.:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.permanentSlNo}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                      <strong>Name of Company:</strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#333' }}>
                      {headerData.companyName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-4">


                <div className="block-c-type-section">
                  <label className="form-label mb-2" style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '8px' }}>
                    Block C Type:
                  </label>
                  <div className="d-flex gap-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="blockCType"
                        id="grossBlock"
                        value="grossBlock"
                        checked={selectedBlockType === "grossBlock"}
                        onChange={(e) => setSelectedBlockType(e.target.value)}
                        style={{ cursor: 'pointer', marginTop: '4px' }}
                      />
                      <label className="form-check-label" htmlFor="grossBlock" style={{ cursor: 'pointer', fontSize: '14px', marginLeft: '4px' }}>
                        Gross Block
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="blockCType"
                        id="netBlock"
                        value="netBlock"
                        checked={selectedBlockType === "netBlock"}
                        onChange={(e) => setSelectedBlockType(e.target.value)}
                        style={{ cursor: 'pointer', marginTop: '4px' }}
                      />
                      <label className="form-check-label" htmlFor="netBlock" style={{ cursor: 'pointer', fontSize: '14px', marginLeft: '4px' }}>
                        Net Block
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Table Styles */}
        <style>
          {`
            .table-responsive {
              max-width: 100%;
              overflow: visible !important;
            }
            .table {
              width: 100%;
              table-layout: fixed;
              max-width: 100%;
            }
            .table th {
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
              font-weight: 600;
              font-size: 14px;
              text-align: center;
              vertical-align: middle;
              padding: 8px 4px;
              word-wrap: break-word;
              word-break: break-word;
              white-space: normal;
              line-height: 1.3;
            }
            .table td {
              border: 1px solid #dee2e6;
              padding: 6px 4px;
              font-size: 13px;
              overflow: visible;
              text-overflow: ellipsis;
            }
            .table input {
              border: 1px solid #e0e0e0;
              background: white;
              width: 100%;
              text-align: center;
              font-size: 13px;
              border-radius: 6px;
              padding: 8px 12px;
              transition: all 0.3s ease;
              touch-action: manipulation;
            }
            .table input:focus {
              outline: none;
              border-color: #f75226;
              transform: translateY(-1px);
            }
            .table input:disabled {
              background-color: #f8f9fa;
              border-color: #dee2e6;
              color: #6c757d;
              cursor: not-allowed;
            }
            .table select {
              border: 1px solid #e0e0e0;
              background: white;
              width: 100%;
              text-align: center;
              font-size: 13px;
              border-radius: 6px;
              padding: 8px 12px;
              transition: all 0.3s ease;
              padding-right: 35px;
            }
            .table select:focus {
              outline: none;
              border-color: #f75226;
              transform: translateY(-1px);
            }
            .status-field {
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
              padding: 6px 8px;
              font-size: 12px;
              text-align: center;
              min-width: 120px;
            }
            .custom-dropdown {
              -webkit-overflow-scrolling: touch;
              touch-action: pan-y;
            }
            .custom-dropdown > div {
              -webkit-tap-highlight-color: rgba(0,0,0,0);
            }
            .position-relative {
              -webkit-tap-highlight-color: transparent;
            }
          `}
        </style>

        {/* Main Content Area */}
        <div className="card mb-3" style={{ position: 'relative', zIndex: 1 }}>
          <div className="card-body" style={{ overflow: 'visible', position: 'relative', paddingBottom: '50px' }}>

            <div className="row">
              <div className="col-12">
                <div className="table-responsive" style={{ overflow: 'visible', position: 'relative', zIndex: 1 }}>
                  <div className="d-flex">
                    {/* Main Data Table */}
                    <div className="flex-grow-1" style={{ position: 'relative', zIndex: 1, width: '100%' }}>
                      <table className="table table-bordered mb-0" style={{ position: 'relative', zIndex: 1, marginBottom: '0', width: '100%', tableLayout: 'fixed' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '3%', minWidth: '40px' }}>SL No.</th>
                            <th style={{ width: '15%', minWidth: '150px' }}>Types of Assets</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Gross value opening</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Addition due to revaluation</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Actual Addition</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Deductions / Adjustment</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Closing AS On</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Depreciation beginning</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Depreciation during the year</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Adjustment for sold/discarded during the year</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Net value Year End</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Net value opening</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>Net Value closing</th>
                            <th style={{ width: '10%', minWidth: '100px' }}>Status</th>
                          </tr>
                          <tr>
                            <th style={{ width: '3%', minWidth: '40px' }}>(1)</th>
                            <th style={{ width: '15%', minWidth: '150px' }}>(2)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(3)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(4)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(5)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(6)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(7)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(8)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(9)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(10)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(11)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(12)</th>
                            <th style={{ width: '6%', minWidth: '80px' }}>(13)</th>
                            <th style={{ width: '10%', minWidth: '100px' }}>(14)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generateTableRows()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Remarks Section */}
        <div className='row'>
          <div className='col-md-6 remarks-section'>

            <Remarks
              remarksData={remarksData}
              setRemarksData={setRemarksData}
              blockItemData={blockItemData}
              filteredBlockItems={filteredBlockItems}
              isLoadingBlockItems={isLoadingBlockItems}
              debouncedBlockItemSearch={debouncedBlockItemSearch}
              loadMoreBlockItemData={loadMoreBlockItemData}
              hasMoreBlockItems={hasMoreBlockItems}
              isLoadingMoreBlockItems={isLoadingMoreBlockItems}
              selectedItemsFromTable={formData.items.filter(item => item && item.trim() !== '')}
              remarksList={remarksList}
            />
          </div>
          <div className='col-md-6'>
            <div className='card'>
              <div className='card-body'>
                <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>
                  <li>In Gross Block : Col-2,5,8,9 are Mandatory</li>
                  <li>In Net Block: Col-5,9,12 are Mandatory</li>
                  <li>Including above if Other item is selected, Description and Block are also Mandatory</li>

                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>

            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleReset}
            >
              Reset
            </button>
            {/* <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button> */}
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/balanceSheet')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={async () => {
                const saved = await handleSaveToAPI();
                if (saved) {
                  navigate('/balanceSheetData');
                }
              }}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>

        {/* Remarks Lists Table */}
        <Remarks__list remarksData={filteredRemarksList} />
      </div>
    </>
  );
}

export default FixedAssets; 